import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { DataTablesModule } from 'angular-datatables';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ChartsModule } from 'ng2-charts';
import { AgmCoreModule } from '@agm/core';
import { NgxSortableModule } from 'ngx-sortable'

import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { DashboardHomeComponent } from './inspector-dashboard/dashboard-home.component';
import { CalendarComponent } from './calendar/calendar.component';
import { InspectionListComponent } from './inspection-list/inspection-list.component';
import { ReportLibraryComponent } from './report-library/report-library.component';
import { SettingsWorkspaceComponent } from './settings/settings-workspace.component';
import { SettingsPageComponent } from './settings/settings-page.component';
import { ChemicalsComponent } from './settings/chemicals/chemicals.component';
import { LeadSourcesComponent } from './settings/lead-sources/lead-sources.component';
import { BranchesComponent } from './settings/branches/branches.component';
import { RolesPermissionsComponent } from './settings/roles-permissions/roles-permissions.component';
import { UsersListComponent } from './settings/users/users-list.component';
import { UserFormComponent } from './settings/users/user-form.component';
import { ChangePasswordComponent } from './settings/change-password/change-password.component';
import { AutoEmailsComponent } from './settings/auto-emails/auto-emails.component';
import { SmtpSettingsComponent } from './settings/auto-emails/smtp-settings.component';
import { PlaceholderInsertComponent } from './settings/placeholder-insert.component';
import { PhotoEditorComponent } from './settings/photo-editor.component';
import { DataExportComponent } from './settings/data-exchange/data-export.component';
import { DataImportComponent } from './settings/data-exchange/data-import.component';
import { DataExchangePageComponent } from './settings/data-exchange/data-exchange-page.component';
import { SystemFormComponent } from './settings/system-form/system-form.component';
import { SignIntegrationComponent } from './settings/sign-integration/sign-integration.component';
import { LockoutSettingsComponent } from './settings/lockout/lockout-settings.component';
import { SystemCardComponent } from './settings/system-form/system-card.component';
import { InspectorDashboardComponent } from './inspector-dashboard/inspector-dashboard.component';
import { AdminDashboardComponent } from './admin-dashboard/admin-dashboard.component';
import { SetupGuideComponent } from './setup-guide/setup-guide.component';
import { DashboardLayoutPresetsComponent } from './shell/dashboard-layout-presets.component';
import { GuidedTourComponent } from './guided-tour/guided-tour.component';
import { AppRoutingModule } from './/app-routing.module';
import { DashboardComponent } from './dashboard/dashboard.component';

import { FooterComponent } from './footer/footer.component';
import { ReportWidgetsModule } from './modules/reports/report-widgets.module';
import { LoginComponent } from './login/login.component';
import { IntakeModule } from './modules/intake/intake.module';
import { PopupsModule } from './modules/popups/popups.module';
import { TransactionsModule } from './modules/transactions/transactions.module';
import { ChatModule } from './modules/chat/chat.module';

import { ErrorInterceptor } from './extras/error.interceptor';
import { SampleInspectionInterceptor } from './extras/sample-inspection.interceptor';
import { JwtInterceptor } from './extras/jwt.interceptor';
import { QuickWidgetFormModalComponent } from './shared/components/quick-widget-form-modal/quick-widget-form-modal.component';
import { QuickWidgetComponent } from './shared/components/quick-widget/quick-widget.component';
import { SweetAlert2Module } from '@toverux/ngx-sweetalert2';
import { ClickOutsideModule } from 'ng-click-outside';
import { WantsEarlierComponent } from './shared/components/wants-earlier/wants-earlier.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { GroupWidgetsComponent } from './group-widgets/group-widgets.component';
import { ShareWidgetsComponent } from './shared/components/share-widgets/share-widgets.component';
import { SotableWidgetComponent } from './shared/components/sotable-widget/sotable-widget.component';
import { ReportSettingsComponent } from './components/report-settings/report-settings.component';
import { DiagramComponent } from './components/diagram/diagram.component';

import { TinymceModule } from 'angular2-tinymce';
// angular2-tinymce only bundles its own default plugin set, so pull in pagebreak
// ourselves - it lets report editors insert a real page break (Insert > Page break)
import 'tinymce/plugins/pagebreak/plugin.js';
import { InspectionReportSettingComponent } from './components/inspection-report-setting/inspection-report-setting.component';
import { BillingReportSettingComponent } from './components/billing-report-setting/billing-report-setting.component';
import { LockoutModule } from './modules/lockout/lockout.module';
import { LockboxModule } from './modules/lockbox/lockbox.module';
import { FeesModule } from './components/office-fees/fees.module';
import { SettingsModule } from './modules/settings/settings.module';
import { SharedModule } from './shared/shared.module';
import { NgxDnDModule } from '@swimlane/ngx-dnd';
import { InspectionsIncompleteComponent } from './shared/components/inspections-incomplete/inspections-incomplete.component';
import { InspectionsNeedPaymentComponent } from './shared/components/inspections-need-payment/inspections-need-payment.component';
import { InspectionsReadyToSendComponent } from './shared/components/inspections-ready-to-send/inspections-ready-to-send.component';
import { AddCannedReplyComponent } from './components/canned-replies/add-canned-reply/add-canned-reply.component';
import { EmailTemplatesModule } from './modules/email-templates/email-templates.module';
import { ConfirmationModalComponent } from './common/confirmation-modal/confirmation-modal.component';
import { OnlineSchedulingComponent } from './shared/components/online-scheduling/online-scheduling.component';
import { AgentOutreachComponent } from './shared/components/agent-outreach/agent-outreach.component';
import { NewAppWidgetComponent } from './shared/components/new-app-widget/new-app-widget.component';
import { HomeComponent } from './home/home.component';
import { WidgetFormInlineComponent } from './shared/components/widget-form-inline/widget-form-inline.component';
import { HomeWidgetComponent } from './shared/components/home-widget/home-widget.component';


@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        SidebarComponent,
        DashboardHomeComponent,
        InspectorDashboardComponent,
        AdminDashboardComponent,
        SetupGuideComponent,
        DashboardLayoutPresetsComponent,
        GuidedTourComponent,
        CalendarComponent,
        InspectionListComponent,
        ReportLibraryComponent,
        SettingsWorkspaceComponent,
        SettingsPageComponent,
        ChemicalsComponent,
        LeadSourcesComponent,
        BranchesComponent,
        RolesPermissionsComponent,
        UsersListComponent,
        UserFormComponent,
        ChangePasswordComponent,
        AutoEmailsComponent,
        SmtpSettingsComponent,
        PlaceholderInsertComponent,
        PhotoEditorComponent,
        DataExportComponent,
        DataImportComponent,
        DataExchangePageComponent,
        SystemFormComponent,
        SystemCardComponent,
        SignIntegrationComponent,
        LockoutSettingsComponent,
        DashboardComponent,
        FooterComponent,
        LoginComponent,
        QuickWidgetFormModalComponent,
        QuickWidgetComponent,
        WantsEarlierComponent,
        GroupWidgetsComponent,
        ShareWidgetsComponent,
        SotableWidgetComponent,
        ReportSettingsComponent,
        DiagramComponent,
        InspectionReportSettingComponent,
        BillingReportSettingComponent,
        InspectionsIncompleteComponent,
        InspectionsNeedPaymentComponent,
        InspectionsReadyToSendComponent,
        AddCannedReplyComponent,
        ConfirmationModalComponent,
        OnlineSchedulingComponent,
        AgentOutreachComponent,
        NewAppWidgetComponent,
        HomeComponent,
        WidgetFormInlineComponent,
        HomeWidgetComponent
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        NgbModule.forRoot(),
        AppRoutingModule,
        DataTablesModule,
        FormsModule,
        ReactiveFormsModule,
        ReportWidgetsModule,   // dashboard/home report widgets; report pages are lazy (app-routing)
        ChartsModule,
        SweetAlert2Module.forRoot(),
        IntakeModule,
        TransactionsModule,
        ChatModule,
        PopupsModule,
        NgxSortableModule,
        ClickOutsideModule,
        LockoutModule,
        LockboxModule,
        FeesModule,
        EmailTemplatesModule,
        SettingsModule,
        TinymceModule.withConfig({
            // Absolute (index.html's <base href="/app/">): a relative path resolved against the page URL,
            // so on a nested route (/canned-replies/1) the skin never loaded and the editor stayed hidden.
            skin_url: '/app/assets/tinymce/skins/lightgray',
            // repeats angular2-tinymce's defaults because this key replaces them wholesale
            plugins: [
                'link',
                'paste',
                'table',
                'advlist',
                'autoresize',
                'lists',
                'code',
                'pagebreak'
            ],
            pagebreak_separator: '<!-- pagebreak -->'
        }),
        AgmCoreModule.forRoot({
            apiKey: 'AIzaSyAD_Cfk1gD8pittyTLzzcLXhGnCDF_aGwo',
            libraries: ['places']
        }),
        NgMultiSelectDropDownModule.forRoot(),
        NgxDnDModule,
        SharedModule

    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        // Last, so a refused sample request reaches pages as ErrorInterceptor's plain message.
        { provide: HTTP_INTERCEPTORS, useClass: SampleInspectionInterceptor, multi: true },
    ],
    bootstrap: [AppComponent],
    entryComponents: [
        QuickWidgetFormModalComponent,
        ConfirmationModalComponent
    ]
})
export class AppModule { }
